import React, { useEffect, useState, useCallback } from "react";
import { Clock, MapPin, CheckCircle2, LogIn, LogOut, Loader2, AlertTriangle } from "lucide-react";
import api from "@/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

// Apr 2026 — Manual attendance card shared by salesman + manager dashboards.
// States:
//   1. Not started → BLUE "Check In" button
//   2. Working     → RED "Check Out" button + live running hours
//   3. Completed   → GREY disabled

const captureGeo = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 6000, maximumAge: 10000 }
    );
  });

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export default function AttendanceCard({ onChanged }) {
  const { lang } = useLang();
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [live, setLive] = useState(0);

  const reload = useCallback(() => {
    setLoading(true);
    api
      .get("/attendance/today")
      .then((r) => setRec(r.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Live hours tick while working
  useEffect(() => {
    if (!rec?.check_in_at || rec?.check_out_at) return;
    const start = new Date(rec.check_in_at).getTime();
    const tick = () => setLive((Date.now() - start) / 3600000);
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [rec]);

  // Apr 2026: Gentle reminder for stragglers still checked in past 9h.
  useEffect(() => {
    if (!rec?.check_in_at || rec?.check_out_at) return;
    const start = new Date(rec.check_in_at).getTime();
    const ms9h = 9 * 60 * 60 * 1000;
    const alreadyWarned = sessionStorage.getItem("att_reminder_" + rec.id);
    if (alreadyWarned) return;
    const remaining = ms9h - (Date.now() - start);
    const fireWarn = () => {
      sessionStorage.setItem("att_reminder_" + rec.id, "1");
      toast.warning(
        lang === "ta" ? "நீங்க இன்னும் செக் இன் ஆகியிருக்கீங்க" : "You are still checked in",
        { description: lang === "ta" ? "வேலை முடிந்திருந்தா செக் அவுட் செய்" : "Tap Check Out if your day is done", duration: 8000 }
      );
    };
    if (remaining <= 0) { fireWarn(); return; }
    const t = setTimeout(fireWarn, remaining);
    return () => clearTimeout(t);
  }, [rec, lang]);

  const doCheckIn = async () => {
    setActing(true);
    try {
      const geo = await captureGeo();
      if (!geo) {
        const ok = window.confirm(lang === "ta"
          ? "GPS கிடைக்கல. GPS இல்லாமல் சரி?"
          : "Could not get GPS. Continue without location?");
        if (!ok) return setActing(false);
      }
      await api.post("/attendance/check-in", { geo });
      toast.success(lang === "ta" ? "செக் இன் வெற்றிகரமாக ✓" : "Checked in successfully ✓");
      reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "ta" ? "செக் இன் தோல்வி" : "Check-in failed"));
    } finally { setActing(false); }
  };

  const doCheckOut = async () => {
    setActing(true);
    try {
      const geo = await captureGeo();
      await api.post("/attendance/check-out", { geo });
      toast.success(lang === "ta" ? "செக் அவுட் வெற்றிகரமாக ✓" : "Checked out successfully ✓");
      reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "ta" ? "செக் அவுட் தோல்வி" : "Check-out failed"));
    } finally { setActing(false); }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-3xl p-5 soft-shadow flex items-center gap-3" data-testid="att-card-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#0055FF]" />
        <span className="text-sm text-[#86868B]">{lang === "ta" ? "ஏற்றுகிறது…" : "Loading…"}</span>
      </div>
    );
  }

  // State detection
  const hasCheckedIn = !!rec?.check_in_at;
  const hasCheckedOut = !!rec?.check_out_at;
  const state = hasCheckedOut ? "completed" : hasCheckedIn ? "working" : "idle";

  return (
    <div className="space-y-2.5">
      {state === "idle" && (
        <div className="bg-[#FFF9E6] border border-[#FFE58A] text-[#7A5C00] rounded-2xl px-4 py-3 flex items-center gap-2.5 soft-shadow-sm fade-up" data-testid="att-check-in-banner">
          <AlertTriangle className="w-4 h-4 shrink-0 text-[#D4A017]" />
          <div className="text-sm font-semibold leading-snug">
            {lang === "ta"
              ? "இன்றைய வேலையை தொடங்க செக் இன் செய்"
              : "Please check in to start your day"}
          </div>
        </div>
      )}
      <div
      className={
        "rounded-3xl p-5 soft-shadow-md text-white " +
        (state === "idle"      ? "bg-gradient-to-br from-[#0055FF] to-[#5856D6]" :
         state === "working"   ? "bg-gradient-to-br from-[#FF9500] to-[#FF3B30]" :
                                 "bg-gradient-to-br from-[#6B7280] to-[#9CA3AF]")
      }
      data-testid="att-card"
      data-state={state}
    >
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-white/15 grid place-items-center shrink-0">
          {state === "completed" ? <CheckCircle2 className="w-6 h-6" /> : <Clock className="w-6 h-6" />}
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">
            {lang === "ta" ? "வருகை" : "Attendance"}
          </div>
          <div className="heading-font text-xl font-bold">
            {state === "idle" && (lang === "ta" ? "தொடங்கல" : "Not started")}
            {state === "working" && (lang === "ta" ? "வேலை நடக்குது" : "Working")}
            {state === "completed" && (lang === "ta" ? "முடிந்தது" : "Completed")}
          </div>
        </div>
        {state === "working" && (
          <div className="text-right" data-testid="att-card-live-hours">
            <div className="text-[10px] uppercase opacity-80 font-bold">{lang === "ta" ? "நேரம்" : "Hours"}</div>
            <div className="heading-font text-2xl font-bold">{live.toFixed(1)}</div>
          </div>
        )}
        {state === "completed" && (
          <div className="text-right" data-testid="att-card-total-hours">
            <div className="text-[10px] uppercase opacity-80 font-bold">{lang === "ta" ? "மொத்த நேரம்" : "Total"}</div>
            <div className="heading-font text-2xl font-bold">{(rec?.total_hours ?? 0).toFixed(1)}</div>
          </div>
        )}
      </div>

      {/* Times row */}
      {(hasCheckedIn || hasCheckedOut) && (
        <div className="mt-4 grid grid-cols-2 gap-3 text-center bg-white/10 rounded-2xl p-2.5" data-testid="att-card-times">
          <div>
            <div className="text-[10px] uppercase opacity-80 font-bold">{lang === "ta" ? "செக் இன்" : "Check-in"}</div>
            <div className="font-bold text-sm">{fmtTime(rec?.check_in_at)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase opacity-80 font-bold">{lang === "ta" ? "செக் அவுட்" : "Check-out"}</div>
            <div className="font-bold text-sm">{fmtTime(rec?.check_out_at)}</div>
          </div>
        </div>
      )}

      {/* Action */}
      <div className="mt-4">
        {state === "idle" && (
          <button onClick={doCheckIn} disabled={acting} data-testid="att-card-checkin-btn"
            className="w-full bg-white text-[#0055FF] hover:bg-white/95 disabled:opacity-60 rounded-2xl py-3.5 font-bold text-base flex items-center justify-center gap-2">
            {acting ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5" />}
            {lang === "ta" ? "செக் இன்" : "Check In"}
          </button>
        )}
        {state === "working" && (
          <button onClick={doCheckOut} disabled={acting} data-testid="att-card-checkout-btn"
            className="w-full bg-white text-[#FF3B30] hover:bg-white/95 disabled:opacity-60 rounded-2xl py-3.5 font-bold text-base flex items-center justify-center gap-2">
            {acting ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogOut className="w-5 h-5" />}
            {lang === "ta" ? "செக் அவுட்" : "Check Out"}
          </button>
        )}
        {state === "completed" && (
          <div className="w-full bg-white/10 rounded-2xl py-3.5 font-bold text-center text-base opacity-90 flex items-center justify-center gap-2" data-testid="att-card-completed">
            <CheckCircle2 className="w-5 h-5" />
            {lang === "ta" ? "இன்றைய வேலை முடிந்தது" : "Done for Today"}
            {rec?.auto_check_out && <span className="ml-1 text-[10px] uppercase opacity-80">· {lang === "ta" ? "தானியங்கி" : "Auto"}</span>}
          </div>
        )}
      </div>

      {/* GPS hint (idle only) */}
      {state === "idle" && (
        <div className="mt-2 text-[10px] opacity-75 flex items-center gap-1 justify-center">
          <MapPin className="w-3 h-3" /> {lang === "ta" ? "GPS அனுமதி தேவை" : "GPS permission recommended"}
        </div>
      )}
      </div>
    </div>
  );
}

// Exported helper: import in pages that need a pre-action check
export const useAttendanceGate = () => {
  const { lang } = useLang();
  const check = async () => {
    try {
      const r = await api.get("/attendance/today");
      const rec = r.data;
      if (!rec || !rec.check_in_at) {
        toast.error(lang === "ta" ? "முதலில் செக் இன் செய்!" : "Please check in first");
        return false;
      }
      if (rec.check_out_at) {
        toast.error(lang === "ta" ? "இன்றைய வருகை முடிந்தது. நாளை முயற்சி செய்." : "You have already checked out. Try again tomorrow.");
        return false;
      }
      return true;
    } catch {
      return true; // network failure — don't block
    }
  };
  return { check };
};
